import { computed, nextTick, ref, shallowRef, watch } from 'vue'
import { useDebouncedRef } from './useDebouncedRef'
import { useGpxSelectResolvedOption } from './useGpxSelectResolvedOption'
import type {
  GpxRemoteSelectPage,
  UseGpxRemoteSelectOptions,
} from '../remote-select/types'
import { remoteOptionsWithLoadingPlaceholder } from '../remote-select/remote-options'
import { defaultGetItemId } from './gpx-select-option-utils'
import {
  DEFAULT_REMOTE_SELECT_ITEM_SIZE,
  isRemoteSelectEnabled,
  pageCacheKey,
  pagesForRange,
  rangeIsLoaded,
} from '../remote-select/utils'

export type {
  GpxRemoteSelectFetchPage,
  GpxRemoteSelectMeta,
  GpxRemoteSelectPage,
  UseGpxRemoteSelectOptions,
} from '../remote-select/types'

export function useGpxRemoteSelect<T>(options: UseGpxRemoteSelectOptions<T>) {
  const perPage = options.perPage ?? 100
  const debounceMs = options.debounceMs ?? 300
  const itemSize = options.itemSize ?? DEFAULT_REMOTE_SELECT_ITEM_SIZE

  const filterQuery = ref('')
  const { debounced: debouncedQuery } = useDebouncedRef(filterQuery, debounceMs)

  const items = shallowRef<Array<T | undefined>>([])
  const loading = ref(false)
  const hasLoadedOnce = ref(false)
  const totalRecords = ref(0)
  const pageCache = new Map<string, GpxRemoteSelectPage<T>>()
  let loadGeneration = 0
  let lazyLoadPromise: Promise<void> | null = null

  const getItemId = options.getItemId ?? defaultGetItemId<T>

  const { resolvedSelected, cacheSelected } = useGpxSelectResolvedOption<T>({
    modelValue: options.modelValue,
    getItemId,
    resolveSelected: options.resolveSelected,
    findInItems: (value) =>
      items.value.find(
        (entry) => entry != null && getItemId(entry) === value,
      ) ?? null,
  })

  function resetItems(resetOptions?: { loading?: boolean; clearSelected?: boolean }) {
    loadGeneration += 1
    pageCache.clear()
    totalRecords.value = 0
    items.value = []
    if (resetOptions?.clearSelected) {
      resolvedSelected.value = null
    }
    hasLoadedOnce.value = false
    loading.value = resetOptions?.loading ?? false
  }

  function ensureTotalSlots(total: number) {
    if (total <= 0) {
      items.value = []
      return
    }
    if (items.value.length === total) {
      return
    }
    items.value = Array.from({ length: total }, (_, index) => items.value[index])
  }

  function applyCachedPage(page: number, query: string) {
    const response = pageCache.get(pageCacheKey(query, page))
    if (!response || totalRecords.value <= 0) {
      return
    }

    const total = totalRecords.value
    const start = (page - 1) * perPage
    const next = items.value.slice()
    let changed = false

    for (let i = 0; i < response.data.length; i += 1) {
      const index = start + i
      if (index >= total) {
        break
      }
      const item = response.data[i]
      if (item == null || next[index] === item) {
        continue
      }
      next[index] = item
      changed = true
    }

    if (changed) {
      items.value = next
    }
  }

  function applyCachedPages(pages: number[], query: string) {
    for (const page of pages) {
      applyCachedPage(page, query)
    }
  }

  function isPageCached(page: number, query: string) {
    return pageCache.has(pageCacheKey(query, page))
  }

  async function fetchPages(
    pages: number[],
    query: string,
    generation: number,
  ): Promise<boolean> {
    const pagesToFetch = pages.filter((page) => !isPageCached(page, query))
    if (pagesToFetch.length === 0) {
      return true
    }

    const fetched = await Promise.all(
      pagesToFetch.map(async (page) => {
        const response = await options.fetchPage({ page, perPage, query })
        return { page, response }
      }),
    )

    if (generation !== loadGeneration) {
      return false
    }

    for (const { page, response } of fetched) {
      pageCache.set(pageCacheKey(query, page), response)
      totalRecords.value = response.meta.total
      ensureTotalSlots(response.meta.total)
    }

    return true
  }

  async function onLazyLoad(event: { first: number; last: number }) {
    if (!isRemoteSelectEnabled(options.enabled)) {
      return
    }

    const first = event.first
    const last = Math.max(event.last, first + 1)
    const query = debouncedQuery.value.trim()

    if (rangeIsLoaded(items.value, first, last)) {
      loading.value = false
      return
    }

    const pages = pagesForRange(first, last, perPage, totalRecords.value)
    if (pages.length === 0) {
      return
    }

    const uncachedPages = pages.filter((page) => !isPageCached(page, query))
    if (uncachedPages.length === 0) {
      applyCachedPages(pages, query)
      hasLoadedOnce.value = true
      loading.value = false
      return
    }

    if (lazyLoadPromise) {
      await lazyLoadPromise
      if (rangeIsLoaded(items.value, first, last)) {
        return
      }
      const stillUncached = pages.filter((page) => !isPageCached(page, query))
      if (stillUncached.length === 0) {
        applyCachedPages(pages, query)
        hasLoadedOnce.value = true
        loading.value = false
        return
      }
    }

    const generation = loadGeneration
    loading.value = true

    lazyLoadPromise = (async () => {
      try {
        const ok = await fetchPages(pages, query, generation)
        if (!ok || generation !== loadGeneration) {
          return
        }
        applyCachedPages(pages, query)
        hasLoadedOnce.value = true
      } finally {
        loading.value = false
        lazyLoadPromise = null
      }
    })()

    await lazyLoadPromise
  }

  async function loadInitialPage() {
    await onLazyLoad({ first: 0, last: 1 })
  }

  function onFilter(event: { value: string }) {
    filterQuery.value = event.value ?? ''
  }

  function onOverlayShow() {
    if (!isRemoteSelectEnabled(options.enabled)) {
      return
    }
    if (!hasLoadedOnce.value) {
      loading.value = true
    }
    void loadInitialPage()
  }

  watch(debouncedQuery, async () => {
    if (!isRemoteSelectEnabled(options.enabled)) {
      return
    }
    resetItems({ loading: true })
    await nextTick()
    await loadInitialPage()
  })

  watch(
    () => isRemoteSelectEnabled(options.enabled),
    (enabled) => {
      if (!enabled) {
        resetItems({ clearSelected: true })
      }
    },
  )

  const optionsForSelect = computed(() =>
    remoteOptionsWithLoadingPlaceholder(
      items.value,
      hasLoadedOnce.value,
      loading.value,
      resolvedSelected.value != null,
    ),
  )

  const virtualScrollerOptions = computed(() => ({
    lazy: true,
    itemSize,
    showLoader: true,
    loading: loading.value,
    delay: 0,
    onLazyLoad,
  }))

  const showEmptyMessage = computed(
    () =>
      hasLoadedOnce.value && totalRecords.value === 0 && !loading.value,
  )

  return {
    options: optionsForSelect,
    onFilter,
    virtualScrollerOptions,
    loading,
    hasLoadedOnce,
    showEmptyMessage,
    resolvedSelected,
    totalRecords,
    filterQuery,
    debouncedQuery,
    onLazyLoad,
    loadInitialPage,
    onOverlayShow,
    cacheSelected,
  }
}
